import type { CSSProperties, HTMLAttributes, ReactNode } from "react";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import type {
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
} from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import type { TextFieldProps } from "@mui/material/TextField";
import TextField from "@mui/material/TextField";
import type { FilterOptionsState } from "@mui/material/useAutocomplete";
import isNaN from "lodash/isNaN";
import sortBy from "lodash/sortBy";
import { matchSorter } from "match-sorter";

export type DropdownOption<T = Record<string, unknown>> = {
  id: string;
  displayName: string;
  group?: string | number;
  style?: CSSProperties;
  order?: number;
  icon?: ReactNode;
  endIcon?: ReactNode;
  disabled?: boolean;
  additionalProps?: T;
};

export type FlexibleOption = {
  id: string | number;
  name?: string | null;
  displayName?: string;
  group?: string | number;
  style?: CSSProperties;
  order?: number;
  icon?: ReactNode;
  endIcon?: ReactNode;
  disabled?: boolean;
  additionalProps?: Record<string, unknown>;
};

const AUTOCOMPLETE__MORE_ID = "__MORE__";
export const AUTOCOMPLETE_PLACEHOLDER_ID = "__PLACEHOLDER__";

const MAX_OPTIONS_DISPLAY = 80;
export const filterOptions = (
  options: DropdownOption[],
  state: FilterOptionsState<DropdownOption>,
  renderCountLimit: number | false = MAX_OPTIONS_DISPLAY,
): DropdownOption[] => {
  const { inputValue } = state;
  const filtered: DropdownOption[] = matchSorter(options, inputValue, {
    keys: ["displayName", "id"],
    // TODO is this breaking the sort order?
    baseSort: (a, b) => {
      if (a.item.id === AUTOCOMPLETE_PLACEHOLDER_ID) return 1;
      return a.index > b.index ? 1 : -1;
    },
  });

  if (renderCountLimit === false) return filtered;

  const shown = filtered.slice(0, renderCountLimit);
  if (filtered.length > shown.length) {
    shown.push({
      displayName: `and ${filtered.length - shown.length} more...`,
      id: AUTOCOMPLETE__MORE_ID,
    });
  }

  return shown;
};

export const getOptionLabel = (option: DropdownOption | string) => {
  if (typeof option === "string") return option;
  return option.displayName;
};

export const isOptionEqualToValue = (option: DropdownOption | string, value: DropdownOption | string) => {
  const isOptionString = typeof option === "string";
  const isValueString = typeof value === "string";

  if (isOptionString && isValueString) return option === value;
  if (!isOptionString && isValueString) return option.displayName === value;
  if (isOptionString && !isValueString) return option === value.displayName;
  if (!isOptionString && !isValueString) return String(option.id) === String(value.id);
  return false;
};

export const renderOptionSolo = (props: HTMLAttributes<HTMLLIElement>, option: DropdownOption) => {
  if (option.id === AUTOCOMPLETE_PLACEHOLDER_ID || option.id === AUTOCOMPLETE__MORE_ID) {
    const { ...otherProps } = props;
    return (
      <li {...otherProps} key={`SystemSiteSelect-option-${option.id}`}>
        {option.displayName}
      </li>
    );
  }
  return (
    <li {...props} key={`SystemSiteSelect-option-${option.id}`}>
      {option?.icon}
      {option.displayName}
      {option?.endIcon}
    </li>
  );
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon color="primary" fontSize="small" />;

export const renderOption = (
  props: HTMLAttributes<HTMLLIElement>,
  option: DropdownOption,
  { selected }: AutocompleteRenderOptionState,
) => {
  if (option.id === AUTOCOMPLETE_PLACEHOLDER_ID || option.id === AUTOCOMPLETE__MORE_ID) {
    const { ...otherProps } = props;
    return (
      <li {...otherProps} key={`SystemSiteSelect-option-${option.id}`} style={option.style}>
        {option.displayName}
      </li>
    );
  }

  return (
    <li {...props} key={`SystemSiteSelect-option-${option.id}`} style={option.style}>
      <Checkbox icon={icon} checkedIcon={checkedIcon} sx={{ marginRight: 2 }} checked={selected} />
      {option?.icon}
      {option.displayName}
      {option?.endIcon}
    </li>
  );
};

export const renderInput = (
  params: AutocompleteRenderInputParams,
  label: string,
  textFieldParams?: TextFieldProps,
  // new-password is an alternative to pass into this if you are having issues
  autoComplete = "off",
) => {
  return (
    <TextField
      {...params}
      {...textFieldParams}
      label={label}
      variant="outlined"
      slotProps={{
        htmlInput: {
          ...params.inputProps,
          autoComplete,
        },
      }}
    />
  );
};

export const dropDownOptionToNumericId = (option: DropdownOption | null): number | null => {
  if (option === null) return option;
  if (isNaN(Number(option.id))) {
    return null;
  }
  return Number(option.id);
};

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

export const dropDownOptionsToNumericIds = (options: DropdownOption[]) => {
  return options.map(dropDownOptionToNumericId).filter(notEmpty);
};

type FlexibleDropdownOptionToDropdownOption = (option: FlexibleOption) => DropdownOption;

export const flexibleOptionToDropdownOption: FlexibleDropdownOptionToDropdownOption = ({
  id,
  name,
  displayName,
  group,
  style,
  order,
  icon,
  endIcon,
  disabled,
  additionalProps,
}) => {
  return {
    id: String(id),
    displayName: displayName ?? name ?? String(id),
    group,
    style,
    order,
    icon,
    endIcon,
    disabled,
    additionalProps,
  };
};

export const flexibleDropdownOptionsToDropdownOptions = (
  options: readonly FlexibleOption[],
  sortKey: keyof DropdownOption | null,
): DropdownOption[] => {
  const dropdownOptions = options.map(flexibleOptionToDropdownOption);
  if (sortKey) {
    return sortBy(dropdownOptions, [sortKey]);
  }

  return dropdownOptions;
};
